import { WithdrawalType } from "@/graphql/__generated__/graphql-operations";
import { GroupedWithdrawals } from "@/types";
import { TxResult, WithdrawalCardModals } from "@/constants/enums";
import { claimInterest, withdraw } from "@/blockchain";
import { useCompanyData, useFinancierData } from "@/hooks";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FramerButton, ServiceProviderDetails } from "@/components/shared";
import { formatNumberLocale } from "@/utils/helpers";
import { toast } from "react-toastify";
import { useExecuteWithdrawalOnInvestmentMutationHook } from "@/hooks/mutations";
import { useIntl } from "react-intl";
import { propsWithdrawalSummaryModal } from "../props";
import { useTranslation } from "react-i18next";

type Props = propsWithdrawalSummaryModal & {
  showModal: WithdrawalCardModals;
};

function WithdrawalSummaryModal(props: Props) {
  // Props
  const {
    showModal,
    setResultModalType,
    activeGroupedWithdrawals,
    setShowModal,
    setTransactionHash,
    activeTotalWithdrawalAmount,
  } = props;

  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["modals", "components", "common", "toasts"]);
  const companyData = useCompanyData();
  const financierData = useFinancierData();

  const { executeWithdrawalOnInvestmentMutation } = useExecuteWithdrawalOnInvestmentMutationHook({ withdrawal: activeGroupedWithdrawals });

  // Function to handle smart contract withdrawal
  async function handleSmartContractWithdrawal(transaction: GroupedWithdrawals | null) {
    if (!financierData || !companyData || !transaction) return;
    try {
      setShowModal(WithdrawalCardModals.Loading);

      const investments = financierData.investments;
      // Find the investment with the matching ID
      const matchingInvestment = investments.find((investment: { _id: string; }) => investment._id === transaction.investmentID);
      if(!matchingInvestment) return;

      let success = false;
      let transactionHash = "";

      if(transaction.type === WithdrawalType.Investment) {
        [success, transactionHash] = await withdraw(companyData.publickey, matchingInvestment.assetfinancingpool.pooladdress as string, matchingInvestment.amount);
      } else {
        [success, transactionHash] = await claimInterest(companyData.publickey, matchingInvestment.assetfinancingpool.pooladdress as string);
      }

      setTransactionHash(transactionHash);

      if(!success) {
        throw new Error("Transaction unsuccessful");
      }

      await executeWithdrawalOnInvestmentMutation({
        variables: {
          id: transaction.investmentID,
          withdrawalType: transaction.type
        }
      }).catch((err) => console.log(err));

      setResultModalType(TxResult.SUCCESS);
      return;
    } catch (_err) {
      setResultModalType(TxResult.ERROR);
      toast.dismiss();
      toast.error(t("toasts:errorOccurred"), {toastId: "errorOccurred"});
    } finally {
      setShowModal(WithdrawalCardModals.WithdrawalResult);
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setShowModal(WithdrawalCardModals.NoModal);
    }
  };

  return (
    <Dialog open={showModal === WithdrawalCardModals.WithdrawalSummary} onOpenChange={handleOpenChange}>
      <DialogContent className="px-6 max-w-3xl max-h-[90vh] overflow-y-auto dark:bg-mfdarklight"
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-lg dark:text-white">{t("withdrawalsummary.title")}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 pt-2 overflow-y-auto">
          {/* Subtext */}
          <div data-cy="withdrawalsummary-subtext" className="mb-4">
            <p className="dark:text-white">
              {t("withdrawalsummary.textbefore")}
              <span className="pl-1 font-semibold">
                {formatNumberLocale(intl, activeTotalWithdrawalAmount , "currency")}
              </span> {t("withdrawalsummary.textafter")}:
            </p>
          </div>

          {/* SP Details */}
          <ServiceProviderDetails
            serviceProviderId={activeGroupedWithdrawals?.spid ?? ""}
          />

          {/* Disclaimer */}
          <div data-cy="withdrawalsummary-disclaimer" className="rounded bg-mfpurple p-4 mt-4 mb-8 text-sm">
            <p className="text-white font-semibold">
              {t("withdrawalsummary.note.title")}:
            </p>
            <p className="text-white">
              {t("withdrawalsummary.note.text")}
            </p>
          </div>
        </div>

        <DialogFooter>
          {/* Buttons */}
          <div data-cy="withdrawalsummary-buttons" className="flex items-center justify-between w-full">
            <FramerButton
              onClick={() => setShowModal(WithdrawalCardModals.MakeWithdrawal)}
              variant="outline-primary"
            >
              {t("common:buttons.back")}
            </FramerButton>
            <FramerButton
              onClick={() => handleSmartContractWithdrawal(activeGroupedWithdrawals)}
              className="bg-mforange text-white hover:bg-mforange/80"
            >
              {t("common:buttons.submit")}
            </FramerButton>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default WithdrawalSummaryModal;
